import { EInvoiceAddressSchemeType } from '@components'

import { notify } from '@billy/design-system'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'

import { useUpdateContactFieldsById } from '@features/contacts/hooks/useUpdateContactFieldsById'
import { Contact } from '@features/contacts/types/contact'
import { XRechnungEInvoicingAddressForm } from '@features/invoices/types/xRechnungEInvoicingAddressForm'

import { NotificationKeys } from '../../../../../enums/notificationKeys'
import { QueryKeys } from '../../../../../enums/queryKeys'
import { APIResponse } from '../../../../../utils'
import { useInvoiceEditorActions } from '../contexts/invoiceEditorActionsContext'
import { useInvoiceEditorForm } from '../contexts/invoiceEditorFormContext'

export const useSubmitXRechnungEInvoicingAddressForm = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { submit } = useInvoiceEditorActions()
  const { watch, setValue } = invoiceEditorForm
  const receiverId = watch('receiver.id')

  const handleUpdateContactFieldsByIdSuccess = async (data: APIResponse<Contact>) => {
    const contact = data.contacts[0]
    const contactId = contact.id
    await queryClient.invalidateQueries([QueryKeys.Contact, contactId])
    await queryClient.invalidateQueries(QueryKeys.Contacts)
    submit.downloadAsXRechnung()
  }

  const handleUpdateContactFieldsByIdError = () => {
    notify({ id: NotificationKeys.UpdateContact, message: t('error_saving'), variant: 'error' })
  }

  const { update: updateContactFields } = useUpdateContactFieldsById({
    onSuccess: handleUpdateContactFieldsByIdSuccess,
    onError: handleUpdateContactFieldsByIdError,
  })

  const updateReceiverEInvoicingAddressFieldsAndSubmit = useCallback(
    (xRechnungFormData: XRechnungEInvoicingAddressForm) => {
      const { ean, eInvoiceScheme, publicEntityRoutingEnabled } = xRechnungFormData

      setValue('receiver.ean', ean)
      setValue('receiver.eInvoiceScheme', eInvoiceScheme)
      setValue('receiver.publicEntityRoutingEnabled', publicEntityRoutingEnabled)

      if (eInvoiceScheme === EInvoiceAddressSchemeType.LWID && publicEntityRoutingEnabled) {
        setValue('buyerReference', ean)
      }

      const contactFieldsToUpdate = { ean, eInvoiceScheme, publicEntityRoutingEnabled }

      if (receiverId) {
        updateContactFields({ id: receiverId, fields: contactFieldsToUpdate })
      }
    },
    [receiverId, setValue, updateContactFields],
  )

  return { updateReceiverEInvoicingAddressFieldsAndSubmit }
}
